import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, HandHeart, Heart, Home, MapPin, Sparkles } from "lucide-react";
import api from "../lib/api";
import { Logo, LogoMark } from "../components/Logo";
import { NavMenu } from "../components/NavMenu";
import { Reveal } from "../components/Reveal";
import { SocialBand } from "../components/SocialBand";
import { CardBack } from "../components/TarotCard";
import { ArcanaIcon } from "../components/ArcanaIcon";
import { AceHeart } from "../components/AceHeart";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { useAuth } from "../context/AuthContext";
import { AdminFooterLink } from "../components/AdminFooterLink";

const HERO_LINES = ["Certaines rencontres ne s'expliquent pas.", "Elles se ressentent."];

const HEART_PROFILES = [
  { icon: "chat", label: "Aimer par les mots", text: "Dire ce que l'on ressent, rassurer, complimenter, encourager." },
  { icon: "hands", label: "Aimer par les actes", text: "Prendre soin, aider, être présent sans qu'on ait besoin de demander." },
  { icon: "hourglass", label: "Aimer par le temps partagé", text: "Donner de son temps, créer des souvenirs, profiter pleinement de l'autre." },
  { icon: "heart", label: "Aimer par la tendresse", text: "Les câlins, les bisous, les gestes affectueux, la proximité." },
  { icon: "infinity", label: "Aimer par la présence", text: "Rester là dans les bons comme dans les mauvais moments, écouter et soutenir." },
  { icon: "gift", label: "Aimer par les surprises", text: "Les attentions inattendues, les petites surprises et les élans qui font sourire." },
];

const PILLARS = [
  {
    icon: HandHeart,
    title: "AFFINITÉS",
    text: "Des valeurs, des attentes et une vision du couple qui peuvent réellement se rejoindre.",
  },
  {
    icon: Home,
    title: "VIE QUOTIDIENNE",
    text: "Une mise en relation qui prend en compte votre vie, avec ou sans enfant.",
  },
  {
    icon: MapPin,
    title: "PROXIMITÉ",
    text: "Parce qu'une belle histoire doit aussi pouvoir fonctionner dans la vraie vie.",
  },
];

const HOW_STEPS = [
  { title: "Je réponds", text: "Quelques minutes pour exprimer ce que je recherche réellement." },
  { title: "Apinity recherche mes affinités", text: "Mes réponses sont comparées afin d'identifier les compatibilités importantes." },
  { title: "Je découvre mes matchs", text: "Je découvre des personnes avec lesquelles je pourrais réellement construire quelque chose." },
];

const FAQ = [
  {
    q: "Combien de temps prend le questionnaire ?",
    a: "Environ 5 minutes. 10 questions sur votre personnalité, vos valeurs et votre façon d'aimer — avec 4 cartes à retourner sous chacune et 1 ou 2 réponses à choisir.",
  },
  {
    q: "En quoi APINITY est différent des autres applis ?",
    a: "Pas de photos qui défilent ni de swipe : on part de vos réponses pour mesurer une vraie compatibilité de vie. La rencontre commence par qui vous êtes, pas par une image.",
  },
  {
    q: "Comment est calculée la compatibilité ?",
    a: "Vos réponses sont comparées thème par thème à celles des autres membres. Plus vos choix se rejoignent sur les sujets qui comptent — enfants, proximité, valeurs, quotidien — plus votre score d'affinité est élevé.",
  },
  {
    q: "Comment fonctionne le matching ?",
    a: "Vous parcourez les profils sous forme de cartes à retourner et vous envoyez un coup de cœur. Si l'autre vous choisit aussi, c'est un match : le chat s'ouvre et la conversation commence.",
  },
  {
    q: "Est-ce que le site est sûr et modéré ?",
    a: "Oui. Chaque compte est lié à une adresse email vérifiée, vos données sont protégées, et les comportements inappropriés peuvent être signalés. La modération veille.",
  },
  {
    q: "Est-ce que la distance est vraiment prise en compte ?",
    a: "Oui, vos réponses sur la proximité font partie du calcul de compatibilité. Une belle histoire doit aussi pouvoir fonctionner dans la vraie vie.",
  },
  {
    q: "Est-ce que mes réponses sont confidentielles ?",
    a: "Absolument. Vos réponses détaillées ne sont jamais affichées publiquement : seuls votre profil, votre façon d'aimer et votre score d'affinité sont visibles des autres membres.",
  },
  {
    q: "Puis-je refaire le test plus tard ?",
    a: "Oui, si vous le souhaitez — mais sans aucune obligation : vos réponses sont enregistrées. Votre test débloque 1 mois de matchs et de messagerie ; passé ce délai, prolongez simplement votre accès d'un mois (10 €) pour recevoir de nouveaux matchs.",
  },
  {
    q: "Que se passe-t-il si personne ne me correspond aujourd'hui ?",
    a: "Les matchs naissent des réponses des membres inscrits : de nouveaux profils compatibles arrivent au fil des inscriptions. Votre accès dure 1 mois à partir du paiement — prolongez-le ensuite d'un mois pour continuer à découvrir vos nouveaux matchs.",
  },
  {
    q: "Faut-il payer pour utiliser APINITY ?",
    a: "Le test de compatibilité complet coûte 10 € — une seule fois. Pas d'abonnement, pas de frais cachés : vous payez le test, vous découvrez vos affinités, et vos matchs sont ouverts pendant 1 mois.",
  },
  {
    q: "Qui est derrière APINITY ?",
    a: "Une seule personne : Alexandra, maman solo. Elle a imaginé APINITY de A à Z — des questions du test jusqu'aux petites cartes — avec une conviction : une belle rencontre ne se résume pas à une photo. Et elle lit personnellement tous vos messages dans « Vos avis ».",
  },
];

export default function Landing() {
  const { user } = useAuth();
  const [topAvis, setTopAvis] = useState([]);
  useEffect(() => {
    api.get("/avis").then((r) => setTopAvis(r.data.filter((a) => a.rating >= 4).slice(0, 3))).catch(() => {});
  }, []);
  // Diagnostic temporaire (invisible) : identifie tout élément peint au coin haut-gauche sur l'iPad de la fondatrice
  useEffect(() => {
    if (sessionStorage.getItem("diag-corner")) return;
    sessionStorage.setItem("diag-corner", "1");
    const timer = setTimeout(() => {
      try {
        const points = [[12, 12], [60, 40], [85, 85], [140, 140], [40, 160], [85, 210], [160, 60]];
        const vus = new Map();
        const decrire = (el) => {
          if (!el || el === document.documentElement || el === document.body) return;
          const r = el.getBoundingClientRect();
          const s = getComputedStyle(el);
          const cle = el.tagName + "|" + el.id + "|" + (el.getAttribute("data-testid") || "") + "|" + Math.round(r.x) + "|" + Math.round(r.y);
          if (vus.has(cle)) return;
          vus.set(cle, {
            tag: el.tagName, id: el.id || null, testid: el.getAttribute("data-testid") || null,
            cls: (el.className && el.className.toString ? el.className.toString() : "").slice(0, 140) || null,
            src: el.src || el.href || null,
            bg: s.backgroundColor, pos: s.position, z: s.zIndex, op: s.opacity,
            rect: { x: Math.round(r.x), y: Math.round(r.y), w: Math.round(r.width), h: Math.round(r.height) },
          });
        };
        points.forEach(([x, y]) => document.elementsFromPoint(x, y).forEach(decrire));
        document.querySelectorAll("*").forEach((el) => {
          const s = getComputedStyle(el);
          if (s.position !== "fixed" && s.position !== "absolute") return;
          const r = el.getBoundingClientRect();
          if (r.left < 220 && r.top < 260 && r.width >= 60 && r.height >= 60) decrire(el);
        });
        api.post("/diag/corner", {
          ua: navigator.userAgent, vw: window.innerWidth, vh: window.innerHeight,
          dsr: window.devicePixelRatio, url: window.location.href, elements: Array.from(vus.values()),
        }).catch(() => {});
      } catch (e) { /* silencieux */ }
    }, 4000);
    return () => clearTimeout(timer);
  }, []);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const yCards = useTransform(scrollYProgress, [0, 1], [0, -90]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <div className="grain min-h-screen bg-cream" data-testid="landing-page">
      {/* Nav */}
      <header className="absolute inset-x-0 top-0 z-40">
        <div className="relative mx-auto flex max-w-6xl items-center justify-between px-4 py-5 sm:px-8">
          {/* Tablette : logo Infinity sans fond à gauche ; mobile et ordinateur : logo classique */}
          <span data-testid="landing-logo" className="flex items-center gap-2.5">
            <img src="/assets/infinity-sans-fond.png" alt="APINITY" className="hidden h-11 w-auto md:block min-[1280px]:hidden" />
            <span className="md:hidden min-[1280px]:block"><Logo size={44} /></span>
            <span className="hidden flex-col leading-none md:flex min-[1280px]:hidden">
              <span className="font-cinzel text-lg font-semibold tracking-[0.12em] text-ink">APINITY</span>
              <span className="mt-[3px] font-cinzel text-[7px] tracking-[0.28em] text-terra">HAPPY AFFINITY</span>
            </span>
          </span>
          <div className="flex items-center gap-3">
            {!user && (
              <Link to="/auth" data-testid="nav-login-button" className="hidden rounded-full px-4 py-2.5 text-sm font-semibold text-ink transition-colors duration-300 hover:text-terra sm:inline-block">
                Connexion
              </Link>
            )}
            <NavMenu />
          </div>
        </div>
      </header>

      {/* Hero */}
      <section ref={heroRef} className="relative overflow-hidden pt-28 pb-20 sm:pt-36 sm:pb-28">
        <img src="/assets/couple-hero-soft.jpeg" alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-cream/60 via-cream/30 to-cream" />
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="relative mb-14 w-full"
        >
          <div className="bg-[#4A0810]">
            <picture>
              <source media="(min-width: 1280px)" srcSet="/assets/apinity-brand-hero-wide.jpeg?v=20261016" />
              <source media="(min-width: 768px)" srcSet="/assets/apinity-brand-hero-tablet.jpeg" />
              <img src="/assets/apinity-brand-hero-mobile.jpg?v=20261016" alt="APINITY — des rencontres qui ont du sens" className="h-80 w-full object-cover object-center md:h-auto min-[1280px]:aspect-[2.6/1]" data-testid="hero-brand-image" />
            </picture>
          </div>
        </motion.div>
        <img loading="lazy" decoding="async"
          src="/assets/mandala-arrow.jpeg"
          alt=""
          className="spin-slow pointer-events-none absolute -right-32 -top-32 h-[420px] w-[420px] rounded-full opacity-[0.18]"
        />
        <img loading="lazy" decoding="async"
          src="/assets/mandala-sun.jpeg"
          alt=""
          className="spin-slow pointer-events-none absolute -bottom-40 -left-36 h-[380px] w-[380px] rounded-full opacity-[0.15]"
        />
        <div className="pointer-events-none absolute -left-20 top-24 h-72 w-72 rounded-full bg-[radial-gradient(circle,rgba(227,168,151,0.25)_0%,rgba(227,168,151,0)_70%)]" />
        <div className="relative mx-auto grid max-w-6xl items-center gap-16 px-4 sm:px-8 lg:grid-cols-[1.2fr_1fr]">
          <motion.div style={{ y: yText }}>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="mb-6 max-w-md text-xs font-semibold uppercase tracking-[0.25em] leading-relaxed text-terra"
              data-testid="hero-eyebrow"
            >
              Et si la bonne rencontre commençait par une vraie compatibilité ?
            </motion.p>
            <h1 className="font-serif text-4xl font-medium leading-[1.08] tracking-tight text-ink sm:text-5xl lg:text-6xl" data-testid="hero-title">
              {HERO_LINES.map((line, i) => (
                <span key={i} className="block overflow-hidden pb-1">
                  <motion.span
                    className="block"
                    initial={{ y: "110%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 1, delay: 0.25 + i * 0.18, ease: [0.22, 1, 0.36, 1] }}
                  >
                    {i === 1 ? (<em className="italic text-terra">{line} <Heart className="mb-2 inline-block h-8 w-8 sm:h-10 sm:w-10" fill="#C86D51" /></em>) : line}
                  </motion.span>
                </span>
              ))}
            </h1>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
              className="mt-8 flex items-end justify-start"
              data-testid="hero-rose-card"
            >
              {[
                { src: "/assets/card-bouquet-lune.jpeg", alt: "Carte lune APINITY", rot: -12, delay: 0.7, mr: -14 },
                { src: "/assets/card-back-4.jpeg", alt: "Carte lotus APINITY", rot: -4, delay: 0.62, mr: -14 },
                { src: "/assets/apinity-card-rose.jpeg", alt: "La carte APINITY", rot: 4, delay: 0.55, mr: -14, z: true },
                { src: "/assets/card-bouquet-soleil.jpeg", alt: "Carte soleil APINITY", rot: 12, delay: 0.85, mr: 0 },
              ].map((c, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, rotate: 0, y: 24 }}
                  animate={{ opacity: 1, rotate: c.rot, y: Math.abs(c.rot) > 8 ? 6 : 0 }}
                  transition={{ duration: 0.9, delay: c.delay, ease: [0.22, 1, 0.36, 1] }}
                  className={`float-y w-24 sm:w-28 ${c.z ? "relative z-10" : ""}`}
                  style={{ marginRight: i < 3 ? c.mr : 0, animationDelay: `${i * 0.8}s` }}
                >
                  <div className="overflow-hidden rounded-2xl border border-gold/50 shadow-card">
                    <img loading="lazy" decoding="async" src={c.src} alt={c.alt} className="aspect-[5/8] h-full w-full object-cover" />
                  </div>
                </motion.div>
              ))}
            </motion.div>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7 }}
              className="mt-8 inline-block max-w-md rounded-2xl bg-plum/55 px-5 py-3 font-serif text-lg font-normal italic text-white shadow-boho backdrop-blur-sm"
              data-testid="hero-intro"
            >
              Bienvenue sur APINITY — ici, on ne juge pas une photo, on joue à se découvrir.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.9 }}
              className="mt-6 max-w-lg rounded-3xl border border-rosedust/40 bg-white/85 p-6 text-left shadow-boho backdrop-blur-sm"
              data-testid="hero-subtitle"
            >
              <p className="font-serif text-lg font-medium leading-snug text-ink">
                Deux façons de se rencontrer, une même envie : créer de belles histoires avec APINITY.
              </p>
              <div className="mt-4 space-y-4">
                <div>
                  <p className="font-serif text-base font-semibold text-terra">✨ 1. Le test de compatibilité : laissez parler vos affinités</p>
                  <p className="mt-1 text-sm leading-relaxed text-clay">
                    Répondez à 10 questions à travers un jeu de cartes interactif. Pour chaque question, retournez les 4 cartes proposées et choisissez-en 1 ou 2, selon vos préférences. Découvrez ensuite les affinités qui vous rapprochent des autres membres. Et si l'attirance est réciproque, le chat s'ouvre pour apprendre à vous connaître et, pourquoi pas, provoquer un coup de cœur.
                  </p>
                </div>
                <div>
                  <p className="font-serif text-base font-semibold text-terra">🥂 2. Les soirées Speed Dating : passez du virtuel au réel</p>
                  <p className="mt-1 text-sm leading-relaxed text-clay">
                    Envie de rencontres spontanées, de regards échangés et de conversations autour d'un verre ? Retrouvez-nous à Saint-Germain-en-Laye pour des soirées Speed Dating conviviales, où vous rencontrez plusieurs célibataires en personne, dans une ambiance propice aux échanges et aux coups de cœur.
                  </p>
                </div>
              </div>
              <p className="mt-4 border-t border-rosedust/40 pt-3 font-serif text-sm italic leading-relaxed text-ink">
                💫 APINITY, c'est vous laisser le choix : découvrir vos affinités grâce à un jeu en ligne ou provoquer la rencontre autour d'un verre. Deux expériences différentes, une multitude de possibilités.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.1 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <Link to={user ? "/quiz" : "/auth?mode=register"} data-testid="hero-cta-button" className="group flex items-center gap-2 rounded-full bg-terra px-7 py-3.5 text-sm font-semibold text-cream shadow-card transition-all duration-300 hover:scale-[1.03] hover:bg-terra-dark">
                Commencer le jeu
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </motion.div>
          </motion.div>

          {/* Fanned cards */}
          <motion.div style={{ y: yCards }} className="relative mx-auto hidden h-[420px] w-full max-w-sm lg:block" data-testid="hero-cards">
            {[
              { rot: "-14deg", x: "-52%", delay: "0s" },
              { rot: "0deg", x: "-50%", delay: "1.2s" },
              { rot: "14deg", x: "-48%", delay: "2.4s" },
            ].map((c, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 60, rotate: 0 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.6 + i * 0.2, ease: [0.22, 1, 0.36, 1] }}
                className="absolute left-1/2 top-8 h-[360px] w-[228px]"
                style={{ "--card-rot": c.rot, transform: `translateX(${c.x})`, zIndex: i === 1 ? 2 : 1 }}
              >
                <div className="float-y h-full w-full" style={{ "--card-rot": c.rot, animationDelay: c.delay }}>
                  <CardBack variant={i} />
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Séparateur mandala */}
      <div className="flex items-center justify-center gap-6 py-6" aria-hidden="true">
        <span className="h-px w-24 bg-rosedust" />
        <img loading="lazy" decoding="async" src="/assets/mandala-sun.jpeg" alt="" className="spin-slow h-14 w-14 rounded-full border border-gold/40 object-cover opacity-70" />
        <span className="h-px w-24 bg-rosedust" />
      </div>

      {/* Affinités */}
      <section className="relative mx-auto max-w-6xl overflow-hidden px-4 py-24 sm:px-8" data-testid="pillars-section">
        <img loading="lazy" decoding="async" src="/assets/mandala-sun.jpeg" alt="" className="spin-slow pointer-events-none absolute -left-40 -top-24 h-[380px] w-[380px] rounded-full opacity-[0.18]" />
        <div className="pointer-events-none absolute -right-24 bottom-0 h-80 w-80 rounded-full bg-rosedust/25 blur-3xl" />
        <Reveal>
          <h2 className="max-w-2xl font-serif text-3xl font-medium tracking-tight text-ink sm:text-4xl" data-testid="pillars-title">
            Pas seulement des points communs. <em className="italic text-terra">Des affinités.</em>
          </h2>
          <p className="mt-4 max-w-lg font-serif text-lg italic text-clay">Parce qu'une belle rencontre ne se résume pas à une photo.</p>
        </Reveal>
        <div className="mt-12 grid gap-6 rounded-3xl border border-rosedust/40 bg-white/60 p-6 backdrop-blur-sm sm:grid-cols-3 sm:p-10">
          {PILLARS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.12} className="h-full">
              <div className="flex h-full flex-col rounded-2xl border border-rosedust/50 bg-white p-8 shadow-boho transition-all duration-300 hover:-translate-y-2 hover:border-rosedust hover:shadow-card" data-testid={`pillar-${i + 1}`}>
                <div className="relative mb-6 h-20 w-20">
                  <img loading="lazy" decoding="async" src="/assets/mandala-arrow.jpeg" alt="" className="absolute inset-0 h-full w-full rounded-full object-cover" />
                  <div className="absolute inset-0 rounded-full bg-rosedust/30" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white/85 shadow-sm">
                      <p.icon size={20} className="text-terra" />
                    </span>
                  </div>
                </div>
                <h3 className="font-cinzel text-sm font-semibold tracking-[0.25em] text-ink">{p.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-clay">{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Comment ça fonctionne */}
      <section className="relative overflow-hidden border-y border-rosedust/40 bg-gradient-to-b from-rosedust/20 via-parch to-parch py-24" data-testid="how-section">
        <img loading="lazy" decoding="async" src="/assets/card-back-3-sm.jpeg" alt="" className="pointer-events-none absolute -right-24 top-10 h-[340px] w-[230px] rotate-12 rounded-2xl opacity-[0.15]" />
        <img loading="lazy" decoding="async" src="/assets/mandala-sun.jpeg" alt="" className="spin-slow pointer-events-none absolute -bottom-32 -left-32 h-[360px] w-[360px] rounded-full opacity-[0.10]" />
        <div className="relative mx-auto max-w-6xl px-4 sm:px-8">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-terra">Comment ça fonctionne</p>
            <h2 className="mt-4 font-serif text-3xl font-medium tracking-tight text-ink sm:text-4xl">Une approche simple, mais différente.</h2>
          </Reveal>
          <div className="mt-12 grid gap-6 sm:grid-cols-3">
            {HOW_STEPS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.12} className="h-full">
                <div className="flex h-full flex-col rounded-2xl border border-terra/15 bg-cream/90 p-8 shadow-boho transition-all duration-300 hover:-translate-y-2 hover:shadow-card" data-testid={`how-step-${i + 1}`}>
                  <span className="font-cinzel text-xs font-semibold tracking-[0.3em] text-gold">0{i + 1}</span>
                  <h3 className="mt-3 font-serif text-2xl font-medium text-ink">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-clay">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="mt-10 text-center font-serif text-xl italic text-ink" data-testid="how-baseline">
              Pas de swipe. Moins de perte de temps. <em className="text-terra">Plus de sens.</em>
            </p>
          </Reveal>
          <Reveal delay={0.25}>
            <div className="relative mt-10 h-64 overflow-hidden rounded-3xl border border-gold/40 shadow-boho sm:h-80">
              <img loading="lazy" decoding="async" src="/assets/couple-beach.jpeg" alt="Un couple enlacé face à la mer" className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-plum/60 via-plum/10 to-transparent" />
              <p className="absolute bottom-6 left-6 right-6 font-serif text-xl italic text-cream sm:text-2xl">« Les plus belles histoires d'amour commencent souvent par un simple jeu. »</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Façons d'aimer */}
      <section className="relative overflow-hidden py-24" data-testid="archetypes-section">
        <img loading="lazy" decoding="async" src="/assets/mandala-arrow.jpeg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-rosedust/35" />
        <div className="relative mx-auto max-w-6xl px-4 sm:px-8">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-terra">Six façons d'aimer</p>
            <h2 className="mt-4 max-w-xl font-serif text-3xl font-medium tracking-tight text-ink sm:text-4xl">Quelle est votre façon d'aimer ?</h2>
            <p className="mt-4 max-w-lg text-base text-clay">Paroles, gestes, temps, tendresse, présence ou surprises : à la fin du jeu, vos réponses révèlent votre langage d'amour — et c'est lui qui vous rapproche des bonnes personnes.</p>
          </Reveal>
          <div className="mt-12 mx-auto grid max-w-4xl grid-cols-2 gap-4 sm:grid-cols-3">
            {HEART_PROFILES.map((a, i) => (
              <Reveal key={a.label} delay={i * 0.08}>
                <div className="group flex flex-col items-center rounded-2xl border border-terra/15 bg-cream/90 p-6 text-center backdrop-blur-sm transition-all duration-300 hover:-translate-y-2 hover:border-gold/60 hover:shadow-card" data-testid={`archetype-card-${a.icon}`}>
                  <ArcanaIcon name={a.icon} size={56} className="mb-4 transition-transform duration-500 group-hover:scale-110" />
                  <span className="font-serif text-base font-medium leading-tight text-ink">{a.label}</span>
                  <p className="mt-2 text-xs leading-relaxed text-clay">{a.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="mx-auto mt-10 max-w-2xl text-center font-serif text-base italic leading-relaxed text-ink" data-testid="archetypes-compat-note">
              Votre façon d'aimer est le cœur de votre compatibilité : elle est comparée à celle des autres membres, et plus vos langages se répondent, plus votre affinité est forte. <em className="text-terra">C'est elle qui choisit les profils que vous découvrirez.</em>
            </p>
          </Reveal>
        </div>
      </section>
      <section className="mx-auto max-w-3xl px-4 py-16 text-center sm:px-8" data-testid="quote-section">
        <Reveal>
          <p className="font-serif text-2xl italic leading-relaxed text-ink sm:text-3xl">
            « On ne cherche pas quelqu'un qui coche toutes les cases.{' '}
            <span className="text-terra">On cherche quelqu'un avec qui les cases ont du sens. »</span>{' '}
            <AceHeart size={20} className="mb-2 inline-block" />
          </p>
        </Reveal>
      </section>

      {/* FAQ */}
      <section className="relative overflow-hidden border-y border-rosedust/40 bg-gradient-to-b from-parch via-rosedust/20 to-parch py-24" data-testid="faq-section">
        <img loading="lazy" decoding="async" src="/assets/mandala-arrow.jpeg" alt="" className="spin-slow pointer-events-none absolute -left-36 top-16 h-[400px] w-[400px] rounded-full opacity-[0.10]" />
        <img loading="lazy" decoding="async" src="/assets/mandala-sun.jpeg" alt="" className="spin-slow pointer-events-none absolute -right-36 bottom-16 h-[400px] w-[400px] rounded-full opacity-[0.10]" />
        <div className="relative mx-auto max-w-3xl px-4 sm:px-8">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-terra">Questions fréquentes</p>
            <h2 className="mt-4 font-serif text-3xl font-medium tracking-tight text-ink sm:text-4xl">Vos questions, nos réponses.</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-10 flex gap-5 rounded-2xl border border-gold/40 bg-white p-7 shadow-boho" data-testid="faq-founder">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border-2 border-gold/50 bg-gold-light">
                <LogoMark size={30} />
              </div>
              <div>
                <p className="font-serif text-lg italic leading-relaxed text-ink">
                  « Bonjour, c'est Alexandra. Vous vous posez peut-être quelques questions avant de vous lancer — c'est tout à fait normal.
                  J'ai rassemblé ici les plus fréquentes. Et si la vôtre n'y est pas, dites-le-moi dans « Vos avis », je réponds personnellement. »
                </p>
                <p className="mt-3 font-cinzel text-xs font-semibold tracking-[0.25em] text-terra">ALEXANDRA · FONDATRICE</p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <Accordion type="single" collapsible className="mt-10 rounded-2xl border border-terra/15 bg-white px-6 shadow-boho">
              {FAQ.map((item, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-terra/10" data-testid={`faq-item-${i + 1}`}>
                  <AccordionTrigger className="py-5 font-serif text-lg font-medium text-ink hover:text-terra hover:no-underline">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="pb-5 text-sm leading-relaxed text-clay">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* Avis des membres */}
      {topAvis.length > 0 && (
        <section className="mx-auto max-w-6xl px-4 py-24 sm:px-8" data-testid="landing-avis-section">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-terra">Vos avis</p>
            <h2 className="mt-4 font-serif text-3xl font-medium tracking-tight text-ink sm:text-4xl">Ils ont joué, ils racontent</h2>
          </Reveal>
          <div className="mt-12 grid gap-6 sm:grid-cols-3">
            {topAvis.map((a, i) => (
              <Reveal key={a.user_id} delay={i * 0.1} className="h-full">
                <div className="flex h-full flex-col rounded-2xl border border-terra/15 bg-white p-7 shadow-boho" data-testid={`landing-avis-${i}`}>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Heart key={n} size={14} className={n <= a.rating ? "text-terra" : "text-stone2/30"} fill={n <= a.rating ? "#C86D51" : "none"} />
                    ))}
                  </div>
                  <p className="mt-4 flex-1 font-serif text-base italic leading-relaxed text-ink">« {a.text} »</p>
                  {a.reply && (
                    <p className="mt-3 border-l-2 border-gold pl-3 text-xs italic leading-relaxed text-clay">Réponse d'Alexandra : {a.reply}</p>
                  )}
                  <p className="mt-4 font-cinzel text-xs font-semibold tracking-[0.2em] text-terra">{a.name}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* CTA final */}
      <section className="relative overflow-hidden py-28" data-testid="final-cta-section">
        <img loading="lazy" decoding="async" src="/assets/mandala-sun.jpeg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-b from-cream via-rosedust/25 to-cream" />
        <div className="relative mx-auto max-w-3xl px-4 text-center sm:px-8">
          <Reveal>
            <h2 className="font-cinzel text-3xl font-bold leading-tight tracking-tight text-ink sm:text-5xl">Et si votre prochaine belle histoire commençait ici ?</h2>
            <p className="mx-auto mt-5 max-w-md font-serif text-lg italic text-clay">Dix questions, quelques minutes de jeu. Votre façon d'aimer — et peut-être la bonne personne — vous attendent de l'autre côté des cartes.</p>
            <Link to={user ? "/quiz" : "/auth?mode=register"} data-testid="final-cta-button" className="mt-10 inline-flex items-center gap-2 rounded-full bg-plum px-8 py-4 text-sm font-semibold text-cream shadow-card transition-all duration-300 hover:scale-[1.03] hover:bg-ink">
              Commencer le jeu
              <ArrowRight size={16} />
            </Link>
            <p className="mt-5 inline-block rounded-full bg-plum px-6 py-2.5 text-sm font-semibold text-white shadow-boho" data-testid="price-note"><strong>10 €</strong> — une seule fois, sans abonnement.</p>
            <p className="mt-4 text-sm">
              <Link to="/offrir" data-testid="gift-link" className="font-semibold text-terra underline decoration-gold/60 underline-offset-4 transition-colors hover:text-terra-dark">
                Ou offrez le test à un ami — sa carte mystère arrive par email
              </Link>
            </p>
          </Reveal>
        </div>
      </section>

      {/* Réseaux sociaux */}
      <section className="mx-auto max-w-3xl px-4 pb-16 sm:px-8" data-testid="landing-social">
        <SocialBand />
      </section>

      <footer className="relative overflow-hidden border-t border-terra/10 py-10">
        <img loading="lazy" decoding="async" src="/assets/mandala-sun.jpeg" alt="" className="pointer-events-none absolute left-1/2 top-1/2 h-[300px] w-[300px] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-[0.08]" />
        <div className="relative mx-auto flex max-w-6xl flex-col items-center justify-between gap-6 px-4 sm:flex-row sm:px-8">
          <Logo size={36} />
          <div className="flex flex-wrap items-center justify-center gap-4 text-xs tracking-wide text-stone2">
            <Link to="/contact" data-testid="footer-contact-link" className="transition-colors hover:text-terra">Contact</Link>
            <Link to="/cgv" data-testid="footer-cgv-link" className="transition-colors hover:text-terra">CGV</Link>
            <Link to="/mentions-legales" data-testid="footer-mentions-link" className="transition-colors hover:text-terra">Mentions légales</Link>
            <Link to="/confidentialite" data-testid="footer-confidentialite-link" className="transition-colors hover:text-terra">Confidentialité</Link>
            <Link to="/pua" data-testid="footer-pua-link" className="transition-colors hover:text-terra">PUA</Link>
            <Link to="/protocole-forces-ordre" data-testid="footer-protocole-link" className="transition-colors hover:text-terra">Protocole FDO</Link>
            <AdminFooterLink />
          </div>
          <p className="text-xs tracking-wide text-stone2">© 2026 APINITY — Et si la bonne rencontre commençait par une vraie compatibilité ?</p>
        </div>
      </footer>
    </div>
  );
}
